import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSupplierAuth } from '../../../context/SupplierAuthContext';
import { supplierModel } from '../../../models/supplierModel';
import supplierHelpdeskService from '../../../services/supplierHelpdeskService';
import { getCategories } from '../../../models/helpdeskModel';
import MultiFileInput from '../../../components/common/MultiFileInput';
import { toast } from 'react-toastify';

const SupplierNewTicket = () => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 'medium',
        category: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [attachments, setAttachments] = useState([]);
    const [pastedImages, setPastedImages] = useState([]);
    const [categories, setCategories] = useState([]);
    
    const { supplier } = useSupplierAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await getCategories();
            
            if (response.success || response.status === 1) {
                const data = response.data;
                // Handle nested data structure: data.data.categories or data.categories or data
                const apiCategories = data?.data?.categories || data?.categories || data || [];
                
                // Transform categories to have value and label properties for the dropdown
                const formattedCategories = apiCategories.map(category => ({
                    value: category.category_id || category.value,
                    label: category.category_name || category.label || category.name,
                    ...category
                }));
                
                setCategories(formattedCategories);
                
                // Set default category to first available if none selected
                if (!formData.category && formattedCategories.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        category: formattedCategories[0].value
                    }));
                }
            } else {
                console.warn('Failed to load categories from API, using fallback');
                // Fallback categories if API fails
                const fallbackCategories = [
                    { value: 'general', label: 'General Support' },
                    { value: 'technical', label: 'Technical Issue' },
                    { value: 'access', label: 'Access Request' }
                ];
                setCategories(fallbackCategories);
                if (!formData.category) {
                    setFormData(prev => ({ ...prev, category: 'general' }));
                }
                toast.warn('Using default categories - please contact IT to configure category system');
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            toast.error('Failed to load categories');
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.title.trim()) {
            newErrors.title = 'Subject is required';
        } else if (formData.title.trim().length < 5) {
            newErrors.title = 'Subject must be at least 5 characters long';
        }
        
        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        } else if (formData.description.trim().length < 20) {
            newErrors.description = 'Description must be at least 20 characters long';
        }

        if (!formData.category) {
            newErrors.category = 'Category is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAttachmentsChange = (files) => {
        setAttachments(files);
    };

    const handlePaste = (e) => {
        const clipboardData = e.clipboardData;
        const items = clipboardData.items;
        
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.indexOf('image') !== -1) {
                e.preventDefault();
                const file = item.getAsFile();
                const reader = new FileReader();
                
                reader.onload = (event) => {
                    const imageData = {
                        file: file,
                        preview: event.target.result,
                        name: `pasted-image-${Date.now()}.png`
                    };
                    setPastedImages(prev => [...prev, imageData]);
                };
                
                reader.readAsDataURL(file);
                break;
            }
        }
    };

    const removeImage = (index) => {
        setPastedImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Please fix the errors below');
            return;
        }

        setLoading(true);
        try {
            // Combine regular attachments with pasted images
            const allAttachments = [
                ...attachments,
                ...pastedImages.map(img => img.file)
            ];

            const response = await supplierModel.createHelpdeskTicket({
                title: formData.title.trim(),
                description: formData.description.trim(),
                priority: formData.priority,
                type: 'support', // Default type for supplier tickets
                category: formData.category,
                attachments: allAttachments
            });

            if (response.status === 1) {
                toast.success('Support ticket created successfully!');
                navigate('/supplier/helpdesk');
            } else {
                toast.error(response.message || 'Failed to create ticket');
            }
        } catch (error) {
            console.error('Error creating ticket:', error);
            toast.error('Error creating ticket: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const priorityLevels = supplierHelpdeskService.getPriorityLevels();

    return (
        <div className="p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center mb-4">
                    <Link
                        to="/supplier/helpdesk"
                        className="mr-4 text-gray-600 hover:text-gray-900 transition-colors duration-200"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900">Create Support Ticket</h1>
                </div>
                <p className="text-gray-600">
                    Describe your issue or request in detail, and our support team will get back to you as soon as possible.
                </p>
            </div>

 
            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-white shadow rounded-lg p-6">
                    {/* Subject Field */}
                    <div className="mb-6">
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                            Subject *
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 ${
                                errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="Brief description of your issue or request"
                            maxLength="200"
                        />
                        {errors.title && (
                            <p className="mt-2 text-sm text-red-600">{errors.title}</p>
                        )}
                        <p className="mt-2 text-sm text-gray-500">
                            {formData.title.length}/200 characters
                        </p>
                    </div>

                              {/* Priority Guidelines */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <h3 className="text-sm font-medium text-amber-800 mb-3 flex items-center">
                            <svg className="w-4 h-4 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Priority Guidelines
                        </h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-start">
                                <span className="inline-block w-16 text-red-600 font-medium">High:</span>
                                <span className="text-gray-700">Critical system outages, security issues, or urgent business needs</span>
                            </div>
                            <div className="flex items-start">
                                <span className="inline-block w-16 text-orange-600 font-medium">Medium:</span>
                                <span className="text-gray-700">Issues affecting normal operations but with workarounds available</span>
                            </div>
                            <div className="flex items-start">
                                <span className="inline-block w-16 text-gray-600 font-medium">Low:</span>
                                <span className="text-gray-700">General questions, feature requests, or minor issues</span>
                            </div>
                        </div>
                    </div>

                    {/* Priority and Category Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                                Priority
                            </label>
                            <select
                                id="priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                            >
                                {priorityLevels.map((level) => (
                                    <option key={level.value} value={level.value}>
                                        {level.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                                Service Category *
                            </label>
                            <select
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 ${
                                    errors.category ? 'border-red-300' : 'border-gray-300'
                                }`}
                            >
                                <option value="">Select a category...</option>
                                {categories.map((category) => (
                                    <option key={category.value} value={category.value}>
                                        {category.label}
                                    </option>
                                ))}
                            </select>
                            {errors.category && (
                                <p className="text-red-600 text-sm mt-1">{errors.category}</p>
                            )}
                        </div>
                    </div>

          

                    {/* Description Field */}
                    <div className="mb-6">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                            Description *
                        </label>
                        <div className="relative">
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                onPaste={handlePaste}
                                rows="8"
                                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 resize-vertical ${
                                    errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                }`}
                                placeholder="Please provide detailed information about your issue or request. Include:&#10;• What you were trying to do&#10;• What happened instead&#10;• Any error messages you received&#10;• Steps to reproduce the issue&#10;• Any relevant context or background information&#10;&#10;You can paste screenshots directly here (Ctrl+V or Cmd+V)."
                                maxLength="2000"
                            />
                            <div className="absolute bottom-2 right-2 text-xs text-gray-400 pointer-events-none">
                                📸 Paste screenshots with Ctrl+V
                            </div>
                        </div>
                        {errors.description && (
                            <p className="mt-2 text-sm text-red-600">{errors.description}</p>
                        )}
                        <p className="mt-2 text-sm text-gray-500">
                            {formData.description.length}/2000 characters - The more detail you provide, the faster we can help you.
                        </p>

                        {/* Show pasted images */}
                        {pastedImages.length > 0 && (
                            <div className="mt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium text-gray-700">Pasted Screenshots:</p>
                                    <button
                                        type="button"
                                        onClick={() => setPastedImages([])}
                                        className="text-xs text-red-600 hover:text-red-800 underline"
                                    >
                                        Remove all
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {pastedImages.map((image, index) => (
                                        <div key={index} className="relative group">
                                            <img
                                                src={image.preview}
                                                alt={`Pasted screenshot ${index + 1}`}
                                                className="w-full h-24 object-cover rounded-lg border"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImage(index)}
                                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 transition-all shadow-md border-2 border-white"
                                                title="Remove this image"
                                            >
                                                ×
                                            </button>
                                            <div className="absolute bottom-1 left-1 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded max-w-[90%] truncate">
                                                {image.file.name}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* File Attachments */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Attachments
                        </label>
                        <MultiFileInput
                            files={attachments}
                            onChange={handleAttachmentsChange}
                            multiple={true}
                            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.zip,.xlsx,.pptx"
                            maxSize={10 * 1024 * 1024} // 10MB
                            className="w-full"
                        /> 
                    </div>

                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-end">
                    <Link
                        to="/supplier/helpdesk"
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-center"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Creating Ticket...
                            </>
                        ) : (
                            'Create Ticket'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SupplierNewTicket;